import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ConnectModal, useSuiClient } from "@mysten/dapp-kit";
import { Wallet, Coins } from "lucide-react";
import { useWallet, useWalletBalance } from "@/context/WalletContext";
import { api } from "@/lib/api";
import CoinLogo from "@/components/CoinLogo";
import ReferralCard from "@/components/ReferralCard";
import { fmtNum, fmtSui } from "@/lib/format";

const normType = (t) => {
  if (!t) return "";
  const [addr, ...rest] = t.split("::");
  return addr.replace(/^0x/, "").replace(/^0+/, "") + "::" + rest.join("::");
};

export default function Portfolio() {
  const { connected, address } = useWallet();
  const { realBalance } = useWalletBalance();
  const client = useSuiClient();
  const [data, setData] = useState(null);

  const loadInflightRef = useRef(false);
  // Holdings are read straight from the chain (on-chain launchpad coins the wallet actually holds),
  // not the legacy off-chain DB — that's why real traders used to always see "No holdings yet".
  const load = useCallback(async () => {
    if (!address || loadInflightRef.current) return;
    loadInflightRef.current = true;
    try {
      const [{ data: coins }, balances] = await Promise.all([
        api.get("/coins/onchain"),
        client.listBalances({ owner: address, limit: 500 }).then((r) => r.balances),
      ]);
      const list = Array.isArray(coins) ? coins : (coins?.coins || []);
      const balMap = {};
      for (const b of balances) balMap[normType(b.coinType)] = BigInt(b.balance);
      const holdings = [];
      let totalValue = 0;
      for (const c of list) {
        const base = balMap[normType(c.coin_type)] || 0n;
        if (base <= 0n) continue;
        const dec = c.decimals || 9;
        const balance = Number(base) / 10 ** dec;
        const value = balance * (c.price_sui || 0);
        totalValue += value;
        holdings.push({ ...c, coin_id: c.curve_id, balance, value_sui: value });
      }
      holdings.sort((a, b) => b.value_sui - a.value_sui);
      setData({ holdings, holdings_value_sui: totalValue });
    } catch (e) { setData({ holdings: [], holdings_value_sui: 0 }); }
    finally { loadInflightRef.current = false; }
  }, [address, client]);

  // Debounced + guarded so a settling wallet address doesn't refire the portfolio fetch.
  useEffect(() => {
    if (!address) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [address, load]);

  if (!connected) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <Wallet className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
        <h2 className="font-display font-bold text-xl mb-2">Connect your wallet</h2>
        <p className="text-sm text-muted-foreground mb-5">See your live SUI balance and coin holdings.</p>
        <ConnectModal
          trigger={
            <button className="px-6 py-3 rounded-full font-bold text-black" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>Connect Wallet</button>
          }
        />
      </div>
    );
  }

  if (!data) return <div className="text-center py-20 text-muted-foreground">Loading portfolio...</div>;

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display font-extrabold text-3xl">Portfolio</h1>
        <Link to={`/u/${address}`} data-testid="portfolio-profile-link" className="text-sm font-bold text-[#1EC62E] hover:underline">View / edit profile →</Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card-surface p-5"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Live SUI (mainnet)</div><div className="font-display font-bold text-2xl mt-1 text-[#1EC62E]">{fmtSui(realBalance, 4)}</div></div>
        <div className="card-surface p-5" data-testid="portfolio-tokens-held"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tokens held</div><div className="font-display font-bold text-2xl mt-1 flex items-center gap-1.5 text-white"><Coins className="w-5 h-5 text-[#FFB800]" />{(data.holdings || []).length}</div></div>
        <div className="card-surface p-5"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Holdings value</div><div className="font-display font-bold text-2xl mt-1 text-[#1EC62E]">{fmtSui(data.holdings_value_sui)} SUI</div></div>
      </div>

      <div className="mb-6"><ReferralCard address={address} /></div>

      <h2 className="font-display font-bold text-lg mb-3">Your Coins</h2>
      {data.holdings.length === 0 ? (
        <div className="card-surface p-10 text-center text-muted-foreground text-sm">No holdings yet. <Link to="/" className="text-[#1EC62E]">Explore the board →</Link></div>
      ) : (
        <div className="space-y-2">
          {data.holdings.map((h) => (
              <Link to={`/coin/${h.coin_id}`} key={h.coin_id} className="card-surface p-4 flex items-center gap-4" data-testid={`portfolio-row-${h.coin_id}`}>
                <CoinLogo coin={h} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="font-bold text-sm truncate">{h.name}</span><span className="font-mono text-xs text-[#1EC62E]">${h.symbol}</span>{h.migrated && <span className="text-[9px] px-1.5 py-0.5 rounded text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#FF007A)" }}>Cetus</span>}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">{fmtNum(h.balance)} {h.symbol}</div>
                </div>
                <div className="text-right font-mono">
                  <div className="font-bold text-sm">{fmtSui(h.value_sui, 3)} SUI</div>
                </div>
              </Link>
          ))}
        </div>
      )}
    </div>
  );
}
